import { ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { CloseButton } from '~/components/elements/CloseButton'
import { Tabs } from '~/components/modules/Tabs'
import { genderToGenderCategory } from '~/lib/gender/label'
import { Gender } from '~/lib/gender/types'

type Props = {
  onCloseClick?: () => void
  onGenderChange?: (gender: Gender) => void
  gender: Gender
  children?: ReactNode
}

const switchMenuItems = Object.keys(Gender).map((key) => ({
  key,
  value: genderToGenderCategory(key),
}))

export const ImageSearchList: React.FC<Props> = ({
  onCloseClick,
  onGenderChange,
  gender,
  children,
}) => {
  const listHeaderRef = useRef<HTMLDivElement>(null)
  const [isListSticky, setIsListSticky] = useState(false)
  const listHeaderTouchStart = useCallback(() => {
    if (!isListSticky) return
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [isListSticky])
  const [listBodyMinHeight, setListBodyMinHeight] = useState(0)

  useEffect(() => {
    if (!listHeaderRef.current) return
    setListBodyMinHeight(
      window.innerHeight -
        (listHeaderRef.current ? listHeaderRef.current.offsetHeight : 0)
    )

    const options = {
      rootMargin: `-${listHeaderRef.current.offsetHeight + 1}px 0px 0px 0px`,
      threshold: 0,
    }
    const observer = new IntersectionObserver(([entry]) => {
      if (entry === undefined) return
      setIsListSticky(!entry.isIntersecting)
    }, options)
    observer.observe(listHeaderRef.current)
    return () => observer.disconnect()
  }, [listHeaderRef])

  return (
    <div className="relative z-[2] w-full text-center">
      <div
        ref={listHeaderRef}
        className={`sticky left-0 top-0 z-[1] bg-white transition-[border-radius] duration-[0.05s] ease-linear ${
          !isListSticky && 'rounded-t-[16px]'
        }`}
      >
        <h2
          className="mb-[5px] pb-[9px] pt-[14px] text-[17px] font-bold leading-normal"
          onTouchStart={listHeaderTouchStart}
        >
          似ているアイテム
        </h2>
        {isListSticky && (
          <CloseButton
            onClick={onCloseClick}
            className="absolute left-[2px] top-[2px] p-[10px] text-[23px] text-black-400"
          />
        )}
        <Tabs
          items={switchMenuItems}
          currentKey={gender}
          onChange={onGenderChange}
        />
      </div>
      <div className="bg-white" style={{ minHeight: `${listBodyMinHeight}px` }}>
        {children}
      </div>
    </div>
  )
}
